import React, { useState } from 'react';
import {
  Button,
  Card,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  BannedWordDto,
  CreateBannedWordInput,
  useBannedWords,
  useCreateBannedWord,
  useCreateGlobalBannedWord,
  useDeleteBannedWord,
  useDeleteGlobalBannedWord,
  useGlobalBannedWords,
} from '@shared/api/hooks/useBannedWords';
import { useAuthStore } from '@shared/hooks/useAuthStore';

const CATEGORIES: { value: string; label: string; color: string }[] = [
  { value: 'blasphemy', label: 'Bestemmia', color: 'red' },
  { value: 'insult', label: 'Insulto', color: 'orange' },
  { value: 'slur', label: 'Discriminatorio', color: 'volcano' },
  { value: 'sexual', label: 'Sessuale', color: 'magenta' },
  { value: 'other', label: 'Altro', color: 'default' },
];

function categoryTag(cat: string | null): React.ReactNode {
  if (!cat) return <Typography.Text type="secondary">—</Typography.Text>;
  const found = CATEGORIES.find((c) => c.value === cat);
  return <Tag color={found?.color ?? 'default'}>{found?.label ?? cat}</Tag>;
}

interface DictionaryProps {
  query: UseQueryResult<BannedWordDto[]>;
  createMut: UseMutationResult<BannedWordDto, unknown, CreateBannedWordInput>;
  deleteMut: UseMutationResult<void, unknown, string>;
}

/**
 * Editor riusato per dizionario club e globale (stessa UI, hook diversi).
 * Salviamo la parola in forma base: la normalizzazione (backend) intercetta da
 * sola le varianti mascherate (M3rd4, m_e_r_d_a). La colonna "Forma match"
 * mostra la chiave normalizzata calcolata dal server, sola lettura.
 */
function Dictionary({ query, createMut, deleteMut }: DictionaryProps): React.ReactElement {
  const [word, setWord] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);

  const add = async (): Promise<void> => {
    const trimmed = word.trim();
    if (trimmed.length < 2) {
      message.warning('Inserisci almeno 2 caratteri');
      return;
    }
    try {
      await createMut.mutateAsync({ word: trimmed, category });
      message.success('Parola aggiunta');
      setWord('');
      setCategory(undefined);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Operazione fallita');
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMut.mutateAsync(id);
      message.success('Parola rimossa');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Errore');
    }
  };

  const columns = [
    {
      title: 'Parola',
      dataIndex: 'word',
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Forma match',
      dataIndex: 'normalized',
      render: (v: string) => (
        <Typography.Text code type="secondary">
          {v}
        </Typography.Text>
      ),
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      width: 150,
      render: (v: string | null) => categoryTag(v),
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 90,
      render: (_: unknown, r: BannedWordDto) => (
        <Popconfirm
          title="Rimuovere la parola?"
          okText="Rimuovi"
          okButtonProps={{ danger: true }}
          cancelText="Annulla"
          onConfirm={() => onDelete(r.id)}
        >
          <Button size="small" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      ),
    },
  ];

  const existing = query.data ?? [];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {existing.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {existing.map((item) => {
            const cat = CATEGORIES.find((c) => c.value === item.category);
            return (
              <Tag
                key={item.id}
                color={cat?.color ?? 'default'}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setWord(item.word)}
                title="Clicca per modificare"
              >
                {item.word}
              </Tag>
            );
          })}
        </div>
      )}
      <Space.Compact style={{ width: '100%', maxWidth: 640 }}>
        <Input
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onPressEnter={add}
          placeholder="Parola in forma base (es: parolaccia, insulto...)"
          maxLength={100}
          allowClear
        />
        <Select
          value={category}
          onChange={setCategory}
          placeholder="Categoria"
          allowClear
          style={{ width: 180 }}
          options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={createMut.isPending}
          onClick={add}
        >
          Aggiungi
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} />
      </Space.Compact>

      <Table
        rowKey="id"
        loading={query.isLoading}
        columns={columns}
        dataSource={query.data ?? []}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: 'max-content' }}
        size="small"
        footer={() => (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {query.data?.length ?? 0} parole. La forma mascherata (M3rd4, m_e_r_d_a, mèrdaaa)
            è intercettata automaticamente: salva solo la radice pulita.
          </Typography.Text>
        )}
      />
    </Space>
  );
}

function TenantDictionary(): React.ReactElement {
  return (
    <Dictionary
      query={useBannedWords()}
      createMut={useCreateBannedWord()}
      deleteMut={useDeleteBannedWord()}
    />
  );
}

function GlobalDictionary(): React.ReactElement {
  return (
    <Dictionary
      query={useGlobalBannedWords(true)}
      createMut={useCreateGlobalBannedWord()}
      deleteMut={useDeleteGlobalBannedWord()}
    />
  );
}

export function BannedWordsPage(): React.ReactElement {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const tabs = [
    {
      key: 'tenant',
      label: (
        <Space>
          <SafetyCertificateOutlined />
          Dizionario del club
        </Space>
      ),
      children: <TenantDictionary />,
    },
    ...(isSuperAdmin
      ? [
          {
            key: 'global',
            label: (
              <Space>
                <GlobalOutlined />
                Dizionario globale
              </Space>
            ),
            children: <GlobalDictionary />,
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>Parole vietate (nickname)</span>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary">
          Nickname pubblici (classifica) che contengono queste parole vengono rifiutati alla
          registrazione e alla modifica profilo, incluse le varianti mascherate con numeri o
          simboli. Il <b>dizionario del club</b> si aggiunge a quello <b>globale</b> condiviso da
          tutte le società.
        </Typography.Paragraph>
        <Tabs items={tabs} />
      </Card>
    </div>
  );
}
